import {
  CustomToast,
  TamaguiProvider,
  TamaguiProviderProps,
  ToastProvider,
} from '@mythical/ui'
import { config } from '@mythical/ui-config'
import appConfig from 'app/config'
import { RootStore, RootStoreProvider, setupRootStore, RootStoreModel } from 'app/store'
import { Environment } from 'app/store/environment'
import { UserStoreSnapshot } from 'app/store/user-store'
import { convertNullToUndefined } from 'app/utils/convert-null-to-undefined'
import { getIsStorefrontSupported } from 'app/utils/storefront-detector'
import React, { useEffect, useState, useMemo } from 'react'
import { Hydrate, QueryClient, QueryClientProvider } from 'react-query'

import { NavigationProvider } from './navigation'
import { SafeArea } from './safe-area'
import { ToastViewport } from './ToastViewport'
import { useRefreshUserOnFocus } from 'app/hooks/use-refresh-user-on-focus'

interface ProviderProps extends Omit<TamaguiProviderProps, 'config'> {
  queryState?: any
  initialUserStore?: UserStoreSnapshot | null
}

function UserRefreshOnFocus() {
  useRefreshUserOnFocus()
  return null
}

// Create an initial store synchronously for SSR
function createInitialStore(initialUserStore?: UserStoreSnapshot | null): RootStore {
  try {
    const environment = new Environment(appConfig)
    // Convert null values to undefined for MST compatibility
    const sanitizedUserStore = initialUserStore ? convertNullToUndefined(initialUserStore) : undefined
    const initialData = sanitizedUserStore ? { userStore: sanitizedUserStore } : {}
    return RootStoreModel.create(initialData, environment)
  } catch (e) {
    console.error('Failed to create initial store:', e)
    // Return an empty store as fallback
    const environment = new Environment(appConfig)
    return RootStoreModel.create({}, environment)
  }
}

export function Provider({
  children,
  queryState,
  initialUserStore,
  ...rest
}: ProviderProps) {
  // Create initial store synchronously for SSR hydration
  // Always create a valid store to avoid null reference errors
  const initialStore = useMemo(
    () => createInitialStore(initialUserStore),
    [] // Only create once on mount
  )
  
  const [rootStore, setRootStore] = useState<RootStore>(initialStore)
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 1000 * 60,
          },
        },
      })
  )

  useEffect(() => {
    // On the client, set up the full store with storage persistence
    // This will merge localStorage data with SSR data
    ;(async () => {
      const store = await setupRootStore(appConfig, initialUserStore)

      // Set canMemberfulLink flag based on storefront support
      const canUseMemberfulLinks = await getIsStorefrontSupported()
      store.userStore.setCanMemberfulLink(canUseMemberfulLinks)

      store.setHydrated()
      setRootStore(store)
    })()
  }, [])

  return (
    <QueryClientProvider client={queryClient}>
      <Hydrate state={queryState}>
        <NavigationProvider>
          <RootStoreProvider value={rootStore}>
            <UserRefreshOnFocus />
            <SafeArea>
              <TamaguiProvider
                config={config}
                defaultTheme="light"
                {...rest}
              >
                <ToastProvider
                  swipeDirection="horizontal"
                  duration={6000}
                  native={[

                  ]}
                >
                  {children}
                  <CustomToast />
                  <ToastViewport />
                </ToastProvider>
              </TamaguiProvider>
            </SafeArea>
          </RootStoreProvider>
        </NavigationProvider>
      </Hydrate>
    </QueryClientProvider>
  )
}
